import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import SideMenu from "./SideMenu";
import toast from "react-hot-toast";
import RequireLogin from "../../permissions.js/RequireLogin";
import {
  getApiClient,
  makeStandardApiErrorHandler,
} from "../../server/get_api_client";
import Footer from "../Common/Footer";
import Nav from "../Common/Nav";
import CustomTextField from "../Common/CustomTextField";
import CustomOTP from "../Common/CustomOTP";
import PageLoader from "../Common/PageLoader";

function ChangePassword() {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOTP] = useState("");
  const [otpExpiry, setOTPExpiry] = useState(null);
  const [error, setError] = useState("");
  const [strength, setStrength] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    uppercase: false,
    specialChar: false,
  });
  function getServerMessage(error, fallback = "Something went wrong. Please try again.") {
    const candidates = [
      error?.response?.data, // axios
      error?.response,       // NSwag ApiException — raw string body lands here
      error?.result,         // some NSwag setups
      error?.body,           // fetch wrappers
      error?.data,
    ];

    for (const c of candidates) {
      if (typeof c === "string" && c.trim()) return c;
      if (c && typeof c === "object") {
        if (typeof c.message === "string" && c.message.trim()) return c.message;
        if (typeof c.title === "string" && c.title.trim()) return c.title;
      }
    }
    return fallback;
  }
  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&]).{8,}$/;
    return passwordRegex.test(password);
  };

  const evaluateStrength = (password) => {
    let score = 0;
    const requirements = { ...passwordRequirements };

    if (password.length >= 8) {
      score += 25;
      requirements.minLength = true;
    } else {
      requirements.minLength = false;
    }

    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) {
      score += 50;
      requirements.uppercase = true;
    } else {
      requirements.uppercase = false;
    }


    if (/[@$!%*?&]/.test(password)) {
      score += 25;
      requirements.specialChar = true;
    } else {
      requirements.specialChar = false;
    }

    setPasswordRequirements(requirements);
    setStrength(score);
  };

  const handleNewPasswordChange = (value) => {
    setNewPassword(value);
    evaluateStrength(value);
  };

  const handleSubmit = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("All fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match");
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(
        "Password must be at least 8 characters long, include one uppercase letter, one lowercase letter, and one special character."
      );
      return;
    }

    if (!otp) {
      setError("A confirmation code is required");
      return;
    }

    setIsLoading(true);
    getApiClient()
      .changePassword({

        oldPassword: oldPassword,
        newPassword: newPassword,
        otpCode: otp,
      })
      .then((res) => {
        toast.success("Your password has been changed successfully");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setOTP("");
        setError("");
        setPasswordRequirements({
          minLength: false,
          uppercase: false,
          specialChar: false,
        });
        setStrength(0);
      })
      .catch(makeStandardApiErrorHandler((error) => {
        // error is a plain string like "Not Found: Incorrect Old Password."
        const raw = String(error ?? "").trim();
        let msg = raw || "Unable to change your password. Please try again.";

        // strip the HTTP status prefix ("Not Found: ", "Bad Request: ", etc.)
        msg = msg.replace(
          /^(Not Found|Bad Request|Unauthorized|Forbidden|Conflict|Internal Server Error):\s*/i,
          ""
        );

        setError(msg);
        toast.error(msg);
      }))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    setIsLoading(true);
    getApiClient()
      .getOTPExpiry()
      .then((res) => setOTPExpiry(res?.data?.otpExpiry))
      .catch(makeStandardApiErrorHandler((error) => toast.error(error)))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <>
      <PageLoader isLoading={isLoading} />
      <Nav />
      <Container>
        <TopSection>
          <NavigationInformation>
            Home &gt; My Profile &gt; Change Password
          </NavigationInformation>
          <Title>Security</Title>
        </TopSection>
        <BottomSection>
          <SideMenu />
          <PageInfo>
            <PageInfoTitle>Change Password</PageInfoTitle>
            <Flex>
              <FlexColumn>
                {error && <Error>{error}</Error>}
                <CustomTextField
                  Active={true}
                  Title="*Current Password"
                  Type={"password"}
                  value={oldPassword}
                  onChange={setOldPassword}
                />
                <CustomTextField
                  Active={true}
                  Title="*New Password"
                  Type={"password"}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                />
                <CustomTextField
                  Active={true}
                  Title="*Confirm Password"
                  Type={"password"}
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                />
                <CustomOTP
                  Active={true}
                  Title="*Authentication Code"
                  Type={"number"}
                  otpValue={otp}
                  onOtpChange={setOTP}
                  otpExpiry={otpExpiry}
                />
                <ButtonContainer>
                  <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
                </ButtonContainer>
              </FlexColumn>
              <FlexColumn>
                <PasswordRequirements requirements={passwordRequirements} />
                <PasswordStrengthBar strength={strength} />
              </FlexColumn>
            </Flex>
          </PageInfo>
        </BottomSection>
      </Container>
      <Footer />
    </>
  );
}

const PasswordRequirements = ({ requirements }) => {
  return (
    <Requirements>
      <Requirement>
        {requirements.uppercase ? "✔️" : "❌"} At least one uppercase letter, one lowercase letter.
      </Requirement>
      <Requirement>
        {requirements.specialChar ? "✔️" : "❌"} At least one special character
        from @$!%*?&.
      </Requirement>
      <Requirement>
        {requirements.minLength ? "✔️" : "❌"} A minimum of 8 characters in
        total.
      </Requirement>
    </Requirements>
  );
};

const PasswordStrengthBar = ({ strength }) => {
  const getColor = (score) => {
    if (score <= 25) return "#FF0000"; // Weak
    if (score <= 50) return "#FFA500"; // Moderate
    if (score <= 75) return "#FFFF00"; // Good
    return "#008000"; // Strong
  };

  return (
    <ProgressBar>
      <Progress
        style={{ width: `${strength}%`, backgroundColor: getColor(strength) }}
      />
    </ProgressBar>
  );
};

const Requirements = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Requirement = styled.div`
  font-family: "Museo Sans Cyrl";
  font-size: 14px;
  color: #304961;
`;

const ProgressBar = styled.div`
  width: 250px;
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  margin-top: 10px;
  overflow: hidden;
`;

const Progress = styled.div`
  height: 100%;
  transition: width 0.3s ease-in-out;
`;

const Flex = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 10px;
  min-width: 300px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 85vh;
  gap: 50px;
  padding: 0 5vw;

  @media screen and (max-width: 719px) {
    padding: 0 2vw;
  }
`;

const TopSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  gap: 30px;
`;

const NavigationInformation = styled.div`
  color: var(--darkBlue);
  font-family: "Museo Sans Cyrl";
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
`;

const Title = styled.div`
  color: var(--darkBlue);
  font-family: Georgia;
  font-size: 55px;
  font-weight: 400;
  line-height: 90%;
  letter-spacing: -1.1px;

  @media screen and (max-width: 719px) {
    font-size: 40px;
  }
`;

const BottomSection = styled.div`
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
`;

const PageInfoTitle = styled.div`
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 30px;
  font-weight: 400;
`;

const PageInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  flex: 1;
  height: 100%;
  background-color: #ecf3fb;
  border-radius: 20px;
  padding: 30px 35px;

  @media screen and (max-width: 719px) {
    padding: 20px 15px;
  }
`;

const Error = styled.div`
  text-align: end;
  color: #b20000;
  font-family: "Museo Sans Cyrl";
  font-size: 12px;
  font-style: italic;
  font-weight: 300;
  width: 100%;

  @media (max-width: 768px) {
    text-align: start;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-self: center;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    margin-top: 10px;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  background-color: var(--darkBlue);
  color: #fff;
  text-align: center;
  font-family: "Museo Sans Cyrl";
  font-size: 12px;
  font-weight: 400;
  text-transform: uppercase;
  border-radius: 10px;
  cursor: pointer;
  border: none;

  &:hover {
    background-color: #304961;
  }
`;

export default RequireLogin(ChangePassword);
